<template>
  <transition :css="false" @before-enter="beforeEnter" @enter="enter" @after-enter="runStart">
    <div v-if="show" class="top-progress" :style="barStyle">
      <div class="peg" :style="pegStyle" />
    </div>
  </transition>
</template>

<script lang="ts">
type QueueCallback = (fn: () => void) => void;

function clamp(n: number, min: number, max: number) {
  if (n < min) {
    return min;
  }
  if (n > max) {
    return max;
  }
  return n;
}

const queue = (() => {
  const pending: QueueCallback[] = [];

  function next() {
    const fn = pending.shift();
    if (fn) {
      fn(next);
    }
  }

  return (fn: QueueCallback) => {
    pending.push(fn);
    if (pending.length === 1) {
      next();
    }
  };
})();
</script>

<script lang="ts" setup>
import { sleep } from '@common/utils';
import { computed, ref, type CSSProperties, type Ref } from 'vue';

interface Props {
  speed?: number;
  color?: string;
  colorShadow?: string;
  errorColor?: string;
  trickle?: boolean;
  trickleSpeed?: number;
  easing?: string;
  height?: number;
  minimum?: number;
  maximum?: number;
  zIndex?: number;
}

const props = withDefaults(defineProps<Props>(), {
  speed: 350,
  color: '#29d',
  errorColor: '#f44336',
  trickle: true,
  trickleSpeed: 250,
  easing: 'linear',
  height: 3,
  minimum: 0.8,
  maximum: 97.5,
  zIndex: 9999,
});

const error = ref(false);
const show = ref(false);
const progress = ref(0);
const opacity = ref(1);
const status: Ref<number | null> = ref(null);
const isPaused = ref(false);
const width = ref(0);

const progressColor = computed(() => {
  return error.value ? props.errorColor : props.color;
});

const isStarted = computed(() => {
  return typeof status.value === 'number';
});

const boxShadow = computed(() => {
  return props.colorShadow || progressColor.value;
});

const barStyle = computed((): CSSProperties => ({
  position: 'fixed',
  top: '0',
  left: '0',
  right: '0',
  width: `${progress.value}%`,
  height: `${props.height}px`,
  backgroundColor: progressColor.value,
  transition: `all ${props.speed}ms ${props.easing}`,
  opacity: `${opacity.value}`,
  zIndex: props.zIndex,
}));

const pegStyle = computed((): CSSProperties => ({
  display: 'block',
  position: 'absolute',
  right: '0',
  width: '100px',
  height: '100%',
  opacity: progress.value ? '1' : '0',
  boxShadow: `0 0 10px ${boxShadow.value}, 0 0 5px ${boxShadow.value}`,
  transform: 'rotate(3deg) translate(0px, -4px)',
}));

function beforeEnter() {
  opacity.value = 0;
  progress.value = 0;
  width.value = 0;
}

function enter(_el: any, done: () => void) {
  opacity.value = 1;
  done();
}

function work() {
  setTimeout(() => {
    if (!isStarted.value || isPaused.value) {
      return;
    }
    increase();
    work();
  }, props.trickleSpeed);
}

function runStart() {
  status.value = progress.value === 100 ? null : progress.value;

  if (props.trickle) {
    work();
  }
}

function start() {
  isPaused.value = false;

  if (show.value) {
    runStart();
  } else {
    show.value = true;
  }
}

function set(amount: number) {
  isPaused.value = false;

  let o: number;
  if (isStarted.value) {
    o = amount < progress.value ? clamp(amount, 0, 100) : clamp(amount, props.minimum, 100);
  } else {
    o = 0;
  }

  status.value = o === 100 ? null : o;

  queue(async next => {
    progress.value = o;
    await sleep(props.speed);
    if (o === 100) {
      opacity.value = 0;
      await sleep(props.speed);
      show.value = false;
      error.value = false;
    }
    next();
  });
}

function increase(amount: number | null = null) {
  const o = progress.value;

  if (o < 100 && amount === null) {
    if (o >= 0 && o < 25) {
      amount = Math.random() * 3 + 3;
    } else if (o >= 25 && o < 50) {
      amount = Math.random() * 3;
    } else if (o >= 50 && o < 85) {
      amount = Math.random() * 2;
    } else if (o >= 85 && o < 99) {
      amount = 0.5;
    } else {
      amount = 0;
    }
  }

  if (amount) {
    set(clamp(o + amount, 0, props.maximum));
  }
}

function decrease(amount: number) {
  if (progress.value === 0) {
    return;
  }
  increase(-amount);
}

function done() {
  set(100);
}

function getProgress() {
  return status.value ? progress.value : 0;
}

function pause() {
  isPaused.value = true;
}

function fail() {
  error.value = true;
  done();
}

defineExpose({
  start,
  set,
  increase,
  decrease,
  done,
  getProgress,
  pause,
  fail,
});
</script>
